<template>
  <div id="main-content">
    <Alert v-if="getDetailsFailed || (!isCRM && ((navState?.refData && navState.refData.length === 0) || getRefDataFailed))" alertType="error" />

    <SuccessBanner :header="isClaim ? 'Your claim submission was successful' : 'Your benefit quote submission was successful'" class="banner">
      <div v-if="isClaim" v-html="claimSubmissionInformationText"></div>
    </SuccessBanner>

    <template v-if="!getDetailsFailed && claimMessage && claimMessage.length > 0">
      <Alert v-for="(message, index) in claimMessage" :key="`message-${index}-${message.code}`">
        <p>{{ message.message }}</p>
      </Alert>
    </template>

    <Alert class="alert-quote-success" v-if="!this.successFlag && !this.loading">
      <p v-if="!isClaim">
        Your quote has been successfully submitted. We cannot advise the expected benefit at the moment. All details of the quote will be available in
        <router-link v-if="!isCRM" :to="{ name: BENEFIT_QUOTE_QUOTES }">Recent benefit quotes</router-link>
        <button v-else class="non-button-style" @click="crmNavigatePage(CRM_PAGES.LANDING, {})">Recent benefit quotes</button>
        <span> shortly.</span>
      </p>
    </Alert>

    <h2 id="claim-title">{{ $route.meta.title }}</h2>

    <section v-if="!loading && !getDetailsFailed" id="quote-info">
      <div>
        <div class="box outline">
          <!-- this is being temporarily disabled for R1 -->
          <!-- <div v-if="isClaim">
            <span class="info-title">Claim status</span>
            <p>{{ claimStatus ? toSentenceCase(claimStatus) : '&#8212;' }}</p>
          </div> -->

          <div>
            <h3 class="h6 sub-heading-underlined">{{ isClaim ? 'Claim Overview' : 'Benefit Quote Overview' }}</h3>
          </div>

          <div>
            <span class="info-title">Member receiving services</span>
            <p>{{ memberFullName }}</p>
          </div>

          <div v-if="isClaim">
            <span class="info-title">Payable to</span>
            <p v-for="(item, i) in payableTo" :key="`payable-to-data-${i}`">{{ item }}</p>
            <span class="invisible-title" v-if="payableTo.length <= 0">Pending</span>
          </div>

          <div>
            <span class="info-title">Claim type</span>
            <p>{{ modality }}</p>
          </div>

          <div>
            <span class="info-title">Provider</span>
            <p>{{ providerNumber }}</p>
            <p>{{ providerName }}</p>
            <p>{{ titleCase(providerAddressLine1) }}</p>
            <p>{{ titleCase(providerAddressLine2) }}</p>
            <p>{{ titleCase(providerAddressLine3) }}</p>
            <p>{{ providerAddressLocality }}</p>
          </div>

          <div v-if="!isClaim">
            <span class="info-title">Quote date</span>
            <p>{{ quotationDate.replace(/\//g, ' / ') }}</p>
          </div>
          <div v-else>
            <span class="info-title">Date claim was submitted</span>
            <p>{{ submitted }}</p>
          </div>

          <div class="financial-year-info">
            <div>
              <span v-if="!isClaim" class="info-title">Annual benefit limit</span>
              <span v-else class="info-title">Financial year benefit limit</span>
              <p>{{ getAnnualBenefitLimitDisplayText() }}</p>
              <div v-if="!isClaim" class="note" v-html="annualBenefitLimitHelpText" />
              <div v-else class="note" v-html="claimBenefitLimitHelpText" />
            </div>

            <div>
              <span v-if="!isClaim" class="info-title">Benefits claimed to date</span>
              <span v-else class="info-title">Financial year benefits paid</span>
              <p>
                <template v-if="successFlag">{{ formatAsCurrency(benefitsClaimedToDate) }}</template>
                <span class="invisible-title" v-else>Pending</span>
              </p>
            </div>

            <div>
              <span class="info-title">Total benefits payable</span>
              <p>
                <b v-if="successFlag">{{ formatAsCurrency(totalBenefitsPayable) }}</b>
                <span class="invisible-title" v-else>Pending</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div class="box-wrapper" v-for="claimLine in claimLines" :key="claimLine.serviceId">
          <div class="box outline">
            <div>
              <h3 class="h6 sub-heading-underlined box-heading">{{ claimLine.serviceId }} - {{ claimLine.serviceName }}</h3>
            </div>
            <div v-if="isClaim" class="box-row">
              <span class="info-title">Status</span>
              <p>{{ claimLine.status ? toSentenceCase(claimLine.status) : '&#8212;' }}</p>
            </div>

            <div class="box-row">
              <span class="info-title">Date of service</span>
              <p>{{ reverseFormatFormDate(claimLine.serviceDate) }}</p>
            </div>

            <div v-if="claimLine.toothId && validToothNumber(claimLine.toothId, claimLine.serviceId)" class="box-row">
              <span class="info-title">Tooth number</span>
              <p>{{ claimLine.toothId }}</p>
            </div>

            <div class="box-row">
              <span class="info-title">Provider's fee</span>
              <p>{{ formatAsCurrency(claimLine.providerFee) }}</p>
            </div>

            <!-- this is being temporarily disabled for R1 -->
            <!-- <div class="box-row">
              <span class="info-title">Benefits claimable</span>
              <p v-if="claimLine.benefitsClaimable !== null">{{ formatAsCurrency(claimLine.benefitsClaimable) }}</p>
              <span class="invisible-title" v-else>Pending</span>
            </div> -->

            <div class="box-row info">
              <span class="info-title">Benefits payable</span>
              <p v-if="successFlag">{{ formatAsCurrency(claimLine.benefitsPayable) }}</p>
              <span class="invisible-title" v-else>Pending</span>
            </div>

            <div v-if="isClaim && claimLine.uploadedFiles">
              <div v-for="(filename, i) in claimLine.uploadedFiles" :key="`claim-line-${claimLine.serviceId}-file-${i}-${filename}`" class="box-row">
                <span class="info-title">{{ filename }}</span>
              </div>
            </div>
          </div>

          <template v-if="claimLine.messageLine && claimLine.messageLine.length > 0">
            <Alert
              v-for="(messageLine, index) in filterErrorTypeMessageLines(claimLine.messageLine)"
              :key="`Service ID ${claimLine.serviceId} message ${index}`"
              :message="messageLine.message"
              :alertType="'error'"
            />
          </template>
        </div>

        <div class="content-wrapper">
          <div v-if="!isClaim">
            <p>
              This benefit quote has been saved and is available in your
              <router-link v-if="!isCRM" :to="{ name: BENEFIT_QUOTE_QUOTES }">Recent Benefit Quotes</router-link>
              <button v-else class="non-button-style" @click="crmNavigatePage(CRM_PAGES.LANDING, {})">Recent Benefit Quotes</button>
              <span>.</span>
            </p>
            <p>
              This quote is based on the information you provided, your current cover, your premium paid to date, remaining annual benefit and service frequency limits. Any change
              in these may affect the amount of benefit you receive.
            </p>
            <p v-if="!isCRM">
              If you need assistance, please
              <router-link :to="{ name: CONTACT_US }" target="_blank">get in touch</router-link>
              <span>.</span>
            </p>
          </div>
        </div>

        <div v-if="!isClaim" class="button-wrapper">
          <router-link v-if="!isCRM" :to="{ name: BENEFIT_QUOTE_GET_A_BENEFIT, query: { step: 1 } }" class="button">Get a new quote</router-link>
          <button v-else @click="crmNavigatePage(CRM_PAGES.GABQ)">Get a new quote</button>
        </div>
        <div v-else class="button-wrapper">
          <router-link :to="{ name: CLAIMS }" class="button secondary">Recent claims</router-link>

          <router-link :to="{ name: CLAIMS_MAKE_A_CLAIM }" class="button primary">Make another claim</router-link>
        </div>
      </div>
    </section>

    <ApiLoading v-else :apiError="getDetailsFailed" class="loading-wrapper" :class="getDetailsFailed && !getRefDataFailed ? 'bottom-spacing' : null" isMemberPortal />

    <section id="related-articles" v-if="!isCRM && !loading && !getRefDataFailed && relatedArticles.length > 0">
      <h2>Related articles</h2>
      <QuickLinks :linksData="relatedArticles" />
    </section>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import { VUEX_BENEFIT_QUOTES_CLAIMS, VUEX_GENERAL } from '@/constants/VuexConstants';
import { Alert, ApiLoading, QuickLinks, SuccessBanner } from '@/components';
import BenefitQuotesServices from '@/services/BenefitQuotes';
import ClaimsServices from '@/services/Claims';
import { formatAsCurrency, getAddressLocalityText, titleCase, isBuildCRM, getProductCodeFromModality } from '@/utils';
import { reverseFormatFormDate, toSentenceCase, isEmptyString } from '@general/js/utils';
import { HOME, BENEFIT_QUOTE_GET_A_BENEFIT, BENEFIT_QUOTE_QUOTES, CLAIMS, CLAIMS_MAKE_A_CLAIM, CONTACT_US, ERROR_404_PAGE_NOT_FOUND } from '@/router/routeNames';

import CRMSupportMixins from './CRMSupportMixins';

const isCRM = isBuildCRM();
const UNLIMITED_ANNUAL_BENEFIT_LIMIT = 'Unlimited';

export default {
  name: 'QuoteResultClaimSubmission',

  mixins: [CRMSupportMixins],

  components: {
    Alert,
    ApiLoading,
    QuickLinks,
    SuccessBanner,
  },

  props: {
    isClaim: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isCRM,
      HOME,
      BENEFIT_QUOTE_GET_A_BENEFIT,
      BENEFIT_QUOTE_QUOTES,
      CLAIMS,
      CLAIMS_MAKE_A_CLAIM,
      CONTACT_US,
      getRefDataFailed: false, // Default
      getDetailsFailed: false, // Default
      loading: false, // Default
      navState: window.history.state || {},
      claimStatus: '',
      memberFullName: '',
      modality: '',
      providerNumber: '',
      providerName: '',
      providerAddressLine1: '',
      providerAddressLine2: '',
      providerAddressLine3: '',
      providerAddressLocality: '',
      quotationDate: '',
      annualBenefitLimit: '',
      benefitsClaimedToDate: '',
      totalBenefitsPayable: '',
      claimLines: [],
      successFlag: false,
      payableTo: [],
      submitted: '',
      relatedArticles: [],
      claimMessage: [],
      annualBenefitLimitHelpText: '',
      claimBenefitLimitHelpText: '',
      claimSubmissionInformationText: '',
    };
  },

  beforeMount() {
    // if is not CRM and current policy's `isExtras` flag is false, redirect to member portal homepage
    if (!this.isCRM && !this.currentIsExtrasFlag) {
      this.$router.push({ name: ERROR_404_PAGE_NOT_FOUND });
    }

    /**
     * only fetch if is not CRM and `isExtras` flag is true or is CRM build
     */
    if ((!this.isCRM && this.currentIsExtrasFlag) || this.isCRM) {
      this.fetchData();
    }
  },

  mounted() {
    if (isCRM) {
      this.resetVuex();
    }
  },

  methods: {
    ...mapMutations('BenefitQuotesClaims', {
      resetVuex: VUEX_BENEFIT_QUOTES_CLAIMS.MUTATIONS.resetState,
      updateRefMembers: VUEX_BENEFIT_QUOTES_CLAIMS.MUTATIONS.updateRefMembers,
      updateRefProducts: VUEX_BENEFIT_QUOTES_CLAIMS.MUTATIONS.updateRefProducts,
      updateMappedQuoteProductCodes: VUEX_BENEFIT_QUOTES_CLAIMS.MUTATIONS.updateMappedQuoteProductCodes,
    }),

    async fetchData() {
      // Use the data passed-in via the route params
      const { formData, refData } = !this.isCRM ? this.navState : this.$parent.routeParams;
      const { quoteId, accessingPersonCode } = !this.isCRM ? this.$route.params : this.$parent.routeParams;
      let productCode = !this.isCRM ? this.navState.productCode : this.$parent.routeParams.productCode;

      // Get the productCode from store if productCode is empty
      if (isEmptyString(productCode)) {
        productCode = null;

        // Get productCode if it exists in store
        if (this.mappedProductCodes) {
          productCode = this.mappedProductCodes[quoteId];
        }
      }

      // Display the loading screen
      this.getRefDataFailed = false;
      this.getDetailsFailed = false;

      if (formData === undefined || refData === undefined) {
        this.loading = true;
        // If `formData` or `refData` isn't provided, then make the API calls.
        try {
          const policyNumber = this.isCRM ? this.CRM_POLICY_NUMBER : this.policyNumber;

          const services = [];
          if (this.isClaim) {
            services.push(ClaimsServices.getClaimDetails(quoteId, policyNumber, accessingPersonCode, productCode));
          } else {
            services.push(BenefitQuotesServices.getBenefitQuoteDetails(quoteId, policyNumber, accessingPersonCode, productCode));
          }
          if (!this.isCRM) {
            if (this.isClaim) {
              services.push(ClaimsServices.getMultiStepFormReferenceData());
            } else {
              services.push(BenefitQuotesServices.getMultiStepFormReferenceData());
            }
          }
          if (!this.refData?.products || this.refData.products.length <= 0 || !this.refData?.members || this.refData?.members.length <= 0) {
            if (this.isClaim) {
              services.push(ClaimsServices.getMembersProductsAndModalities(policyNumber));
            } else {
              services.push(BenefitQuotesServices.getMembersProductsAndModalities(policyNumber));
            }
          }

          const data = await Promise.allSettled(services);

          if (data[2] && data[2].value) {
            const productRefData = data[2].value;
            const productList = !this.isClaim ? productRefData?.products : productRefData?.claimsMemberModalities?.products;
            const memberList = !this.isClaim ? productRefData?.members : productRefData?.claimsMemberModalities?.members;
            this.updateRefProducts(productList);
            this.updateRefMembers(memberList);
          }

          // If the API completed successfully, `.value` will exist.
          if (data[0].value) {
            let details = data[0].value;
            this.modality = titleCase(details.modality.claimTypeName);

            // If productCode is empty, call again get claim/benefit quote details after getting the modality name to get the annual benefit limit correctly
            if (isEmptyString(productCode)) {
              // eslint-disable-next-line arrow-body-style
              const memberObject = this.refData.members.find((memberItem) => memberItem.personCode === (details.personCodes ? details.personCodes[0] : details.personCode));
              // eslint-disable-next-line arrow-body-style
              const memberProducts = this.refData.products?.filter((productItem) => {
                return memberObject.products.some((memberProduct) => {
                  return memberProduct.productCode === productItem.productCode;
                });
              });
              memberProducts.forEach((memberProduct) => {
                // eslint-disable-next-line arrow-body-style
                memberProduct.startDate = memberObject.products.find((productItem) => productItem.productCode === memberProduct.productCode).startDate;
                // eslint-disable-next-line arrow-body-style
                memberProduct.endDate = memberObject.products.find((productItem) => productItem.productCode === memberProduct.productCode).endDate;
              });
              productCode = getProductCodeFromModality(this.modality, memberProducts, details.claimLines[0].serviceDate);
              let quoteDetailsData = null;
              if (this.isClaim) {
                quoteDetailsData = await ClaimsServices.getClaimDetails(quoteId, policyNumber, accessingPersonCode, productCode);
              } else {
                quoteDetailsData = await BenefitQuotesServices.getBenefitQuoteDetails(quoteId, policyNumber, accessingPersonCode, productCode);
              }
              details = quoteDetailsData;
            }

            this.updateMappedQuoteProductCodes({ [quoteId]: productCode });
            if (this.isClaim) {
              this.memberFullName = titleCase(details.memberFullNames.join(' & '));
            } else {
              this.memberFullName = titleCase(details.memberFullName);
            }
            this.providerNumber = details.provider.providerId;
            this.providerName = `${details.provider.firstName ? details.provider.firstName : ''} ${details.provider.lastName}`;
            this.providerAddressLine1 = details.provider.addressLine1 ?? '';
            this.providerAddressLine2 = details.provider.addressLine2 ?? '';
            this.providerAddressLine3 = details.provider.addressLine3 ?? '';
            this.providerAddressLocality = getAddressLocalityText(details.provider.suburb ?? '', details.provider.state ?? '', details.provider.postcode ?? '');

            if (this.isClaim) {
              this.successFlag = details.claimSuccessFlag;
              this.submitted = reverseFormatFormDate(details.claimDate);
            } else {
              this.successFlag = details.quoteSuccessFlag;
              this.quotationDate = details.quotationDate.replace(/\//g, ' / ');
            }
            this.claimMessage = details.claimMessage;
            this.annualBenefitLimit = details.modality.isUnlimited ? UNLIMITED_ANNUAL_BENEFIT_LIMIT : details.modality.annualBenefitLimit;
            this.benefitsClaimedToDate = details.modality.consumption;
            this.totalBenefitsPayable = details.modality.totalBenefitsPayable;
            this.claimLines = details.claimLines;

            this.claimStatus = details.claimStatus;
            if (this.isClaim) {
              if (details.claimAccount) {
                this.payableTo = [details.claimAccount.name, details.claimAccount.bsb, details.claimAccount.accountNumber];
              }
            }
          } else {
            this.getDetailsFailed = true;
          }

          if (!this.isCRM) {
            if (data[1].value) {
              const referenceData = data[1].value;
              this.relatedArticles = [referenceData.relatedArticle1, referenceData.relatedArticle2, referenceData.relatedArticle3];
              if (referenceData.annualBenefitLimitHelpText) {
                this.annualBenefitLimitHelpText = referenceData.annualBenefitLimitHelpText.informationText;
              }
              if (referenceData.financialYearBenefitLimitHelpText) {
                this.claimBenefitLimitHelpText = referenceData.financialYearBenefitLimitHelpText.informationText;
              }
              if (referenceData.claimSubmissionInformationText) {
                this.claimSubmissionInformationText = referenceData.claimSubmissionInformationText.informationText;
              }
            } else {
              this.getRefDataFailed = true;
            }
          }
        } catch (error) {
          if (error.message) {
            logger.log(error.message);
          } else {
            logger.log(error);
          }
        } finally {
          // Remove the loading screen
          this.loading = false;
        }
      } else {
        // Use the data passed-in via the route params
        this.memberFullName = titleCase(formData.memberFullName);
        this.modality = titleCase(formData.modality.claimTypeName);
        this.providerNumber = formData.provider.providerId;
        this.providerName = `${formData.provider.firstName} ${formData.provider.lastName}`;
        this.providerAddressLine1 = formData.provider.addressLine1 ?? '';
        this.providerAddressLine2 = formData.provider.addressLine2 ?? '';
        this.providerAddressLine3 = formData.provider.addressLine3 ?? '';
        this.providerAddressLocality = getAddressLocalityText(formData.provider.suburb ?? '', formData.provider.state ?? '', formData.provider.postcode ?? '');

        this.quotationDate = reverseFormatFormDate(formData.quotationDate);

        this.annualBenefitLimit = formData.modality.isUnlimited ? UNLIMITED_ANNUAL_BENEFIT_LIMIT : formData.modality.annualBenefitLimit;
        this.benefitsClaimedToDate = formData.modality.consumption;
        this.totalBenefitsPayable = formData.modality.totalBenefitsPayable;
        this.claimLines = formData.claimLines;
        this.claimMessage = formData.claimMessage;
        if (this.isClaim) {
          this.successFlag = formData.claimSuccessFlag;
        } else {
          this.successFlag = formData.quoteSuccessFlag;
        }

        this.relatedArticles = [refData.relatedArticle1, refData.relatedArticle2, refData.relatedArticle3];

        if (!this.isCRM && refData.annualBenefitLimitHelpText) {
          this.annualBenefitLimitHelpText = refData.annualBenefitLimitHelpText.informationText;
        }
        if (!this.isCRM && refData.financialYearBenefitLimitHelpText) {
          this.claimBenefitLimitHelpText = refData.financialYearBenefitLimitHelpText.informationText;
        }
        if (!this.isCRM && refData.claimSubmissionInformationText) {
          this.claimSubmissionInformationText = refData.claimSubmissionInformationText.informationText;
        }

        this.claimStatus = formData.claimStatus;
        this.payableTo = formData.payableTo;
        this.submitted = reverseFormatFormDate(formData.claimDate);
      }
    },

    /**
     * Gets the annual benefit limit display text.
     */
    getAnnualBenefitLimitDisplayText() {
      if (this.annualBenefitLimit === UNLIMITED_ANNUAL_BENEFIT_LIMIT) {
        return `$ ${UNLIMITED_ANNUAL_BENEFIT_LIMIT}`;
      }
      return formatAsCurrency(this.annualBenefitLimit);
    },

    filterErrorTypeMessageLines(messageLines) {
      // eslint-disable-next-line arrow-body-style
      return messageLines.filter((x) => x.severity === 'F');
    },

    validToothNumber(toothId, serviceId) {
      if (serviceId !== undefined && toothId !== undefined) {
        const id = Number(toothId);
        if (id >= 11 && id <= 85 && toothId.length === 2) {
          return true;
        }
      }
      return false;
    },

    formatAsCurrency,
    titleCase,
    reverseFormatFormDate,
    toSentenceCase,
  },

  computed: {
    ...mapGetters({
      policyNumber: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
      currentIsExtrasFlag: VUEX_GENERAL.GETTERS.getCurrentPolicyIsExtrasFlag,
    }),

    ...mapGetters('BenefitQuotesClaims', {
      refData: VUEX_BENEFIT_QUOTES_CLAIMS.GETTERS.getRefData,
      mappedProductCodes: VUEX_BENEFIT_QUOTES_CLAIMS.GETTERS.getMappedQuoteProductCodes,
    }),
  },
};
</script>

<style lang="scss" scoped>
h1 {
  margin-left: 0;
  margin-right: 0;
  float: none;
}

h2 {
  font-size: font-size(3);
}

:deep(.sub-heading-underlined) {
  margin-bottom: 0.75rem;
}

section {
  margin-bottom: 96px;

  &:last-child {
    margin-bottom: 0;
  }
}

.loading-wrapper {
  min-height: 400px;

  &.bottom-spacing {
    margin-bottom: spacer(10);
  }
}

.banner {
  margin-bottom: 0.5rem;
}

#claim-title {
  margin-block: 35px;
}

#quote-info {
  display: grid;
  grid-template-columns: 30% 65%;
  row-gap: hutter(0.5);
  justify-content: space-between;

  @include mobile {
    grid-template-columns: 1fr;
    row-gap: hutter(0.75);
  }

  .box-wrapper {
    margin-bottom: 36px;
  }

  .note {
    margin-top: 5px;
  }

  .box {
    & > *:not(.icon-container) {
      padding: 0 1.5rem;
      margin-bottom: 0.75rem;

      &:first-child {
        padding-top: 1.5rem;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    &.last-row-grey > *:last-child {
      padding: 5px 20px;
      background: $GREY_50;
      font-weight: $BOLD;
    }

    &.outline {
      border: 1px solid $GREY_300;
    }

    &.stick-to-top {
      border-top: 0;

      ul {
        padding: 0 spacer(2);

        .uploaded-file {
          color: $GREY_700;
          padding: 19px 20px 16px;
          box-shadow: 0 2px 7px 0 rgba(189, 182, 174, 0.5);
          margin-bottom: spacer(1.5);

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }

    .info-title {
      display: block;
      font-weight: $BOLD;
      margin-bottom: 5px;
    }

    .invisible-title {
      opacity: 0.01;
    }

    .note :deep(*) {
      font-size: font-size(-1);
      margin-bottom: 0;
    }

    .box-row {
      @include grid-layout;
      @include grid-template(1fr 1fr);
      margin-bottom: 0.75rem;
      margin-left: 1.5rem;

      & > * {
        margin-bottom: 0;
      }

      &.info span::before {
        content: '';
        position: absolute;
        height: 17.33px;
        width: 17.33px;
        left: -1.65rem;
        top: 50%;
        transform: translateY(-50%);
        background-image: url('/assets/public/images/icon_review_and_submit_info.svg');
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
      }
    }

    .box-row:last-child {
      padding-bottom: 1.5rem;
      margin-bottom: 0;
    }

    .financial-year-info {
      padding: 0;

      & > div {
        padding: 0rem 1.5rem 2rem 1.5rem;

        &:last-child {
          padding-bottom: 1.5rem;
        }
      }
    }

    p {
      margin-bottom: 0;
    }
  }

  .last-row-green > *:last-child {
    padding-bottom: 1.5rem;
    background: $LIGHT_GREEN_TINT;
  }

  .content-wrapper {
    margin-top: hutter(1.25);
    margin-bottom: hutter(1.25);
  }

  .button-wrapper {
    display: flex;
    justify-content: flex-start;

    > .button:first-child {
      margin-right: 1.5rem;
    }

    @include mobile {
      display: grid;
      grid-template-columns: 1fr;
      row-gap: hutter(0.5);
    }

    .button {
      margin: 0;
    }

    > :first-child {
      @include grid-position(1, 1);

      @include mobile {
        @include grid-position(1, 2);
      }
    }

    > :last-child {
      @include grid-position(2, 1);
      justify-self: start;

      @include mobile {
        @include grid-position(1, 1);
      }
    }
  }
}

#related-articles {
  padding-bottom: 56px;
}
</style>
